import { clsx, type ClassValue } from 'clsx'
import { format, formatDistanceToNowStrict, isToday, isYesterday, parseISO } from 'date-fns'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

function toDate(value: string | Date): Date {
  return typeof value === 'string' ? parseISO(value) : value
}

/** 12 Mar 2026 */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—'
  return format(toDate(value), 'd MMM yyyy')
}

/** 12 Mar 2026, 4:30 PM */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—'
  return format(toDate(value), 'd MMM yyyy, h:mm a')
}

/** "Today, 4:30 PM" · "Yesterday" · "3 days ago" · "12 Mar 2026" */
export function formatRelative(value: string | Date | null | undefined): string {
  if (!value) return '—'
  const date = toDate(value)
  if (isToday(date)) return `Today, ${format(date, 'h:mm a')}`
  if (isYesterday(date)) return `Yesterday, ${format(date, 'h:mm a')}`
  const days = (Date.now() - date.getTime()) / 86_400_000
  if (days > 0 && days < 7) return `${formatDistanceToNowStrict(date)} ago`
  return format(date, 'd MMM yyyy')
}

/** Days until a date (negative when past). */
export function daysUntil(value: string): number {
  const target = parseISO(value)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.round((target.getTime() - today.getTime()) / 86_400_000)
}

export function deadlineLabel(value: string): string {
  const days = daysUntil(value)
  if (days < 0) return `Closed ${formatDate(value)}`
  if (days === 0) return 'Closes today'
  if (days === 1) return 'Closes tomorrow'
  if (days <= 7) return `Closes in ${days} days`
  return `Apply by ${formatDate(value)}`
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-IN').format(value)
}

export function formatPercent(value: string | number): string {
  const n = typeof value === 'string' ? Number.parseFloat(value) : value
  if (Number.isNaN(n)) return '—'
  return `${Number.isInteger(n) ? n : n.toFixed(1)}%`
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')).toUpperCase() || '?'
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${formatNumber(count)} ${count === 1 ? singular : plural}`
}

export function greeting(date = new Date()): string {
  const hour = date.getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}
